import { Badge, Box, List, ListItemButton, Typography } from '@mui/material';
import { Fragment, useCallback, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink, Redirect } from 'react-router-dom';
import { appLinks } from '../../constants/navLinks';
import logo_by from '../../resources/img/logo/by.png';
import logo_en from '../../resources/img/logo/en.png';
import logo_ro from '../../resources/img/logo/ro.png';
import logo_ru from '../../resources/img/logo/ru.png';
import logo_ua from '../../resources/img/logo/ua.png';
import logo_ee from '../../resources/img/logo/ее.png';
import ThemeColorSwitch from '../PanelHeader/ThemeColorSwitch';
import Support from '../Support';
import { homePath, hasPanelPermission, panelPermissions } from '../../helpers/panelAccess';
import PanelAccessDenied from '../PanelAccessDenied';
import './Sidebar.css';

const logo = {
    ru: logo_ru,
    ua: logo_ua,
    en: logo_en,
    ee: logo_ee,
    ro: logo_ro,
    by: logo_by,
};

const getPathNameKey = pathname => (pathname || '').split('/')[1] || '';

const Sidebar = props => {
    const { t, i18n } = props;

    const user = useSelector(state => state.user);
    const router = useSelector(state => state.router);
    const { histories } = useSelector(state => state.quiz);

    const quizCounter = useMemo(() => {
        return (histories || []).filter(h => h.status === 'INCOMPLETE' || h.status === 'PASSED').length;
    }, [histories]);

    const emulation = !!user?.emulation?.length;

    const effectivePermissions = useMemo(() => {
        return panelPermissions(user);
    }, [user]);

    const pathNameKey = useMemo(() => {
        return getPathNameKey(router?.location?.pathname);
    }, [router?.location?.pathname]);

    const checkPermission = useMemo(() => {
        return effectivePermissions.includes(pathNameKey);
    }, [effectivePermissions, pathNameKey]);

    const permissionMenuItem = useMemo(() => {
        const perms = panelPermissions(user);
        return perms
            .map(permissionKey => {
                const link = appLinks[permissionKey];
                return link ? { ...link, permissionKey } : null;
            })
            .filter(Boolean);
    }, [user]);

    const menuCheckboxReloader = useCallback(() => {
        const el = document.getElementById('menu__toggle');
        if (el) el.checked = false;
    }, []);

    const currentPath = router?.location?.pathname || '';
    const isRouteActive = useCallback(
        to => {
            if (!to) return false;
            return currentPath === to || currentPath.startsWith(`${to}/`);
        },
        [currentPath]
    );

    const renderMenuItem = useCallback(
        (link, index) => {
            if (!link?.parent) return null;

            const newsCounter = user?.permissionsData?.[link.permissionKey] || 0;
            const isQuizLink = link.permissionKey === 'quizzesStatistic';
            const displayCounter = isQuizLink ? quizCounter : newsCounter;

            const to = link.parent?.to;
            const active = isRouteActive(to);

            const iconActiveNode = !active ? link.iconActive : link.icon;

            const content = (
                <Fragment>
                    {displayCounter > 0 ? (
                        <Badge
                            badgeContent={displayCounter}
                            color={isQuizLink ? 'default' : 'error'}
                            overlap="circular"
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: 1,
                                    ...(isQuizLink && { backgroundColor: '#606060', color: 'white' }),
                                },
                            }}
                        >
                            {iconActiveNode}
                        </Badge>
                    ) : (
                        iconActiveNode
                    )}

                    <Typography variant="sidebar" gutterBottom sx={{ fontWeight: active ? 700 : 400 }}>
                        {t(`${link.title}`)}
                    </Typography>
                </Fragment>
            );

            // 1) external links (href/target)
            if (link.parent?.href) {
                const { to: _to, activeClassName: _ac, ...rest } = link.parent;
                return (
                    <ListItemButton
                        key={index}
                        size="small"
                        variant="sidebar-button"
                        component="a"
                        {...rest}
                        onClick={menuCheckboxReloader}
                        data-testid={`index-listitembutton-item-${index}`}
                    >
                        {content}
                    </ListItemButton>
                );
            }

            // 2) internal route links (to)
            if (to) {
                const { href: _href, onClick: _onClick, ...rest } = link.parent;
                return (
                    <ListItemButton
                        key={index}
                        size="small"
                        variant="sidebar-button"
                        component={NavLink}
                        to={to}
                        activeClassName={link.parent.activeClassName || 'is-active'}
                        selected={active}
                        {...rest}
                        onClick={menuCheckboxReloader}
                        data-testid={`index-listitembutton-item-${index}`}
                    >
                        {content}
                    </ListItemButton>
                );
            }

            // 3) action buttons (onClick)
            if (typeof link.parent?.onClick === 'function') {
                const { to: _to, href: _href, activeClassName: _ac, ...rest } = link.parent;
                return (
                    <ListItemButton
                        key={index}
                        size="small"
                        variant="sidebar-button"
                        {...rest}
                        data-testid={`index-listitembutton-item-${index}`}
                    >
                        {content}
                    </ListItemButton>
                );
            }

            // 4) fallback
            return (
                <ListItemButton
                    key={index}
                    size="small"
                    variant="sidebar-button"
                    {...link.parent}
                    onClick={menuCheckboxReloader}
                    data-testid={`index-listitembutton-item-${index}`}
                >
                    {content}
                </ListItemButton>
            );
        },
        [user?.permissionsData, quizCounter, isRouteActive, menuCheckboxReloader, t]
    );

    return checkPermission ? (
        <Fragment>
            <Box className="sidebar-wrapper">
                <Box className="hamburger-menu">
                    <input id="menu__toggle" type="checkbox" />
                    <label className="menu__btn" htmlFor="menu__toggle">
                        <span />
                    </label>

                    <div className="black_back" onClick={menuCheckboxReloader} />

                    <Box className="sidebar-container menu__box">
                        <div className="header_logo">
                            <img src={logo[i18n.language]} alt="Soroban" />
                        </div>

                        <List className="sidebar-list">
                            {permissionMenuItem.map(renderMenuItem)}

                            {!emulation && hasPanelPermission(user, 'support-chat') && (
                                <Box sx={{ m: '2%', py: '4px' }}>
                                    <Support />
                                </Box>
                            )}

                            <Box sx={{ width: '100%', m: '2%', py: '4px' }}>
                                <ThemeColorSwitch />
                            </Box>
                        </List>
                    </Box>
                </Box>
            </Box>
        </Fragment>
    ) : (
        homePath(user, appLinks) ? <Redirect to={homePath(user, appLinks)} /> : <PanelAccessDenied />
    );
};

export default withTranslation()(Sidebar);
